/**
 * Warehouse-related utility functions
 */

import type { LegacyWarehouse } from "../../utils/warehouseAdapter.js";

/**
 * Get warehouse configuration by warehouse code
 * Falls back to WPBR if the specified warehouse is not found
 * @param code - Warehouse code to look up
 * @returns Warehouse object or null if warehouse data not loaded
 */
export function getWarehouseByCode(
  code?: string,
): LegacyWarehouse | null {
  if (!window.warehouseData || !Array.isArray(window.warehouseData)) {
    console.warn("warehouseData not loaded yet");
    return null;
  }

  const warehouseCode = code ?? "WPBR";
  const warehouse = window.warehouseData.find((w) => w.Code === warehouseCode);

  if (!warehouse && warehouseCode !== "WPBR") {
    console.warn(`Warehouse ${warehouseCode} not found, falling back to WPBR`);
    return window.warehouseData.find((w) => w.Code === "WPBR") ?? null;
  }

  return warehouse ?? null;
}

/**
 * Wait for warehouse data to be loaded with polling mechanism
 * @param callback - Function to call once warehouse data is loaded
 * @param maxAttempts - Maximum number of polling attempts (default: 50)
 */
export function waitForWarehouseData(
  callback: () => void,
  maxAttempts: number = 50,
): void {
  let attempts = 0;

  const checkInterval = setInterval(function () {
    attempts++;

    if (window.warehouseData && Array.isArray(window.warehouseData)) {
      clearInterval(checkInterval);
      callback();
    } else if (attempts >= maxAttempts) {
      clearInterval(checkInterval);
      console.error(
        "warehouseData failed to load after",
        maxAttempts,
        "attempts",
      );
    }
  }, 100);
}
